import { toast } from '@/components/toast';
import { didApiUrl, mixpanelKey, webSocketUrl } from '@/environment';
import { logger } from '@/services/logger';
import { AgentManager, Auth, ChatMode, ConnectionState, createAgentManager, StreamingState } from '@d-id/client-sdk';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';

interface AgentApProps {
    agentId: string;
    videoRef: React.RefObject<HTMLVideoElement>;
    auth: Auth;
}

export const useAgentApi = ({ agentId, videoRef, auth }: AgentApProps) => {
    const [agentAPI, setAgentAPI] = useState<AgentManager | null>(null);
    const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Connecting);
    const [videoState, setVideoState] = useState<StreamingState>(StreamingState.Stop);

    useEffect(() => {
        if (agentAPI) {
            return;
        }

        let agent: AgentManager;

        const createAgent = async () => {
            try {
                agent = await createAgentManager(agentId, {
                    streamOptions: { streamWarmup: false },
                    callbacks: {
                        onSrcObjectReady: (value: MediaProvider | null) => {
                            if (videoRef.current) {
                                videoRef.current.srcObject = value;
                            }
                        },
                        onConnectionStateChange: (state: ConnectionState) => {
                            setConnectionState(state);
                            if (state === ConnectionState.Fail) {
                                logger.error(`Campaign Agent connection failed`, { agentId });
                                toast.error({
                                    title: 'Please try again later.',
                                });
                            }
                        },
                        onVideoStateChange: (state: StreamingState) => {
                            setVideoState(state);
                        },
                    },
                    baseURL: didApiUrl,
                    auth,
                    wsURL: webSocketUrl,
                    distinctId: `campaigns-agent-${nanoid()}`,
                    mode: ChatMode.DirectPlayback,
                    mixpanelKey,
                });
                setAgentAPI(agent);
                await agent.connect();
            } catch (e) {
                logger.error(`Campaign agent Failed to create agent manager`, { agentId });
                toast.error({
                    title: 'Please try again later.',
                });
                setConnectionState(ConnectionState.Fail);
            }
        };

        createAgent();

        return () => {
            setAgentAPI(null);
            agent?.disconnect();
        };
    }, []);

    return {
        agentAPI,
        connectionState,
        videoState,
    };
};
